import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Video, ClipboardList } from "lucide-react";

export default function Register() {
  const { setUser, loadTeam } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [role, setRole] = useState("recorder");
  const [teamMode, setTeamMode] = useState("join"); // join | create
  const [teamCode, setTeamCode] = useState("");
  const [teamName, setTeamName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload = { ...form, role };
    if (teamMode === "join") payload.team_code = teamCode.toUpperCase();
    else payload.team_name = teamName;
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      await loadTeam();
      navigate("/");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const RoleCard = ({ value, icon: Icon, title, desc }) => (
    <button type="button" data-testid={`role-${value}`} onClick={() => setRole(value)}
      className={`flex-1 text-left p-4 rounded-md border-2 transition-all duration-200 ${
        role === value ? "border-primary bg-primary/10" : "border-border bg-card hover:border-muted-foreground/40"}`}>
      <Icon className={`h-6 w-6 mb-2 ${role === value ? "text-primary" : "text-muted-foreground"}`} strokeWidth={2.5} />
      <div className="font-heading font-bold uppercase tracking-tight">{title}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
    </button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-lg fade-up">
        <div className="mb-8"><Logo /></div>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Create account</p>
        <h2 className="font-heading text-4xl font-black uppercase tracking-tight mt-1 mb-8">Join the squad</h2>

        <form onSubmit={submit} className="space-y-5">
          <div className="flex gap-3">
            <RoleCard value="recorder" icon={Video} title="Recorder" desc="Films & tags plays on the field" />
            <RoleCard value="coach" icon={ClipboardList} title="Coach" desc="Reviews clips on the sideline" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Full name</Label>
              <Input data-testid="register-name-input" required value={form.name} onChange={set("name")}
                placeholder="Coach James" className="h-12 bg-card border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
                placeholder="you@team.com" className="h-12 bg-card border-border" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Password</Label>
            <Input data-testid="register-password-input" type="password" required value={form.password} onChange={set("password")}
              placeholder="Min 6 characters" className="h-12 bg-card border-border" />
          </div>

          <div className="flex rounded-md border border-border overflow-hidden">
            <button type="button" data-testid="team-mode-join" onClick={() => setTeamMode("join")}
              className={`flex-1 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${teamMode === "join" ? "bg-secondary text-black" : "bg-card text-muted-foreground"}`}>Join a team</button>
            <button type="button" data-testid="team-mode-create" onClick={() => setTeamMode("create")}
              className={`flex-1 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${teamMode === "create" ? "bg-secondary text-black" : "bg-card text-muted-foreground"}`}>Create a team</button>
          </div>

          {teamMode === "join" ? (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Team code / PIN</Label>
              <Input data-testid="register-team-code-input" required value={teamCode}
                onChange={(e) => setTeamCode(e.target.value.toUpperCase())} placeholder="e.g. COACH1"
                className="h-14 bg-card border-border text-2xl font-heading font-bold tracking-[0.3em] uppercase" maxLength={8} />
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Team name</Label>
              <Input data-testid="register-team-name-input" required value={teamName}
                onChange={(e) => setTeamName(e.target.value)} placeholder="Riverside Eagles" className="h-12 bg-card border-border" />
            </div>
          )}

          {error && <p data-testid="register-error" className="text-sm font-semibold text-primary">{error}</p>}

          <Button type="submit" data-testid="register-submit-button" disabled={loading}
            className="w-full h-12 bg-primary hover:bg-red-500 text-white font-heading font-bold uppercase tracking-widest text-base transition-colors">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-bold text-white hover:text-primary transition-colors">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
